<template>
  <transition v-if="!noContent" :name="transitionName">
    <div
      v-show="isActive"
      v-cloak
      ref="overlay"
      class="overlay critical-hide"
      :class="[
        classContainer,
        $blockClass(blockClass),
        responsiveType,
        `${responsiveType}--${responsiveDirection}`,
      ]"
    >
      <div
        v-if="template"
        ref="scrollElement"
        class="overlay__container"
        :class="[
          $blockClass(blockClass, 'container'),
          $blockClass(responsiveType, 'container'),
        ]"
      >
        <div
          v-if="showClose"
          class="overlay__close-container"
          :class="[
            { 'overlay__close-container--shelf': shelfClose },
            $blockClass(blockClass, 'close-container'),
            $blockClass(responsiveType, 'close-container'),
          ]"
        >
          <button
            class="overlay__close icon-button"
            :class="[
              $blockClass(blockClass, 'close'),
              $blockClass(responsiveType, 'close'),
            ]"
            type="button"
            @click.prevent="close('button')"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.close')"
            />

            <slot name="close">
              <icon-close />
            </slot>
          </button>
        </div>

        <div
          v-if="$slots.title || showClose"
          class="overlay__header"
          :class="[
            {
              'overlay__header--no-title': !$slots.title,
              [$blockClass(blockClass, 'header', 'no-title')]: !$slots.title,
              [$blockClass(responsiveType, 'header', 'no-title')]:
                !$slots.title,
            },
            $blockClass(blockClass, 'header'),
            $blockClass(responsiveType, 'header'),
          ]"
        >
          <p
            v-if="$slots.title"
            class="overlay__title text-mobile-headlines-h3 text-desktop-headlines-h3"
            :class="[
              $blockClass(blockClass, 'title'),
              $blockClass(responsiveType, 'title'),
            ]"
          >
            <slot name="title" />
          </p>
        </div>

        <div
          class="overlay__body"
          :class="[
            $blockClass(blockClass, 'body'),
            $blockClass(responsiveType, 'body'),
            { 'overlay__body--no-margin': !$slots.title },
          ]"
        >
          <slot v-bind="self" name="body" />
        </div>
      </div>

      <!-- If template prop is false then just render body slot -->
      <div
        v-else
        ref="scrollElement"
        class="overlay__container"
        :class="[
          $blockClass(blockClass, 'container'),
          $blockClass(responsiveType, 'container'),
        ]"
      >
        <slot v-bind="self" name="body" />
      </div>
    </div>
  </transition>

  <teleport :disabled="!teleport" to="body">
    <transition name="fade">
      <button
        v-if="isActive && showOverlay"
        class="window-overlay"
        :class="namespace"
        tabindex="-1"
        type="button"
        aria-hidden="true"
        @click.prevent="close('overlay')"
      />
    </transition>
  </teleport>
</template>

<script>
/**
 * Vue: Overlay (overlay)
 * -----------------------------------------------------------------------------
 * Base template for overlays (drawers and modals).
 * - Add `js-overlay="scrollElement"` to element in <slot> to pass that as the
 *   element to disableBodyScroll instead of `.overlay__container`.
 *
 * @param {Boolean} [autoQueue] - Automatically opened overlays are queued.
 * @param {Number} [autoShow] - Time in ms to wait before auto-showing overlay.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} [classContainer] - Class applied to container.
 * @param {String} direction - Open animation direction set using transition
 * styles, `up` or `down` for `modal` and `left` or `right` for `drawer`,
 * supports a combination in the format [mobile direction]-[desktop direction].
 * @param {Boolean} [disableScroll] - Trigger `disableBodyScroll` on open.
 * @param {String} namespace - Unique namespace for overlay, reference when
 * opening/closing the overlay.
 * @param {Boolean} [noContent] - Displays no content, only window overlay, you
 * must manually disable scroll and trap focus in the component which triggers
 * this overlay.
 * @param {Boolean} shelfClose - Renders the button above the shelf container
 * to close the shelf.
 * @param {Boolean} [showClose] - Show the close button, useful for when you
 * want to bind custom events to when the overlay closes.
 * @param {Boolean} [showOverlay] - Show the window overlay?
 * @param {Boolean} [teleport] - Teleport window overlay to end of <body>.
 * @param {Boolean} [template] - Render with default templating.
 * @param {String} type - Type of overlay, `modal`, `drawer`, or `shelf`,
 * supports a combination in the format [mobile type]-[desktop type].
 *
 */
import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock';
import { mapActions, mapState } from 'pinia';
import useIndexStore from '~stores/index';
import useAccessibilityStore from '~stores/accessibility';
import useOverlaysStore from '~stores/overlays';

import IconClose from '~icons/directional-navigation/close.svg';

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Overlay',

  components: {
    IconClose,
  },

  props: {
    autoQueue: {
      type: Boolean,
      default: true,
    },
    autoShow: {
      type: Number,
      default: 0,
    },
    blockClass: {
      type: String,
      default: '',
    },
    classContainer: {
      type: String,
      default: '',
    },
    direction: {
      type: String,
      default: 'up',
      required: true,
      validator: (value) => {
        return value
          .split('-')
          .every((part) => ['up', 'down', 'left', 'right'].includes(part));
      },
    },
    disableScroll: {
      type: Boolean,
      default: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    noContent: {
      type: Boolean,
      default: false,
    },
    shelfClose: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    teleport: {
      type: Boolean,
      default: true,
    },
    template: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'modal',
      required: true,
      validator: (value) => {
        return value
          .split('-')
          .every((part) => ['drawer', 'modal', 'shelf'].includes(part));
      },
    },
    ignoreClose: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cnvsGlobal: true,
      scrollElement: false,
    };
  },

  computed: {
    ...mapState(useIndexStore, {
      hydrated: 'hydrated',
      isMobile: (store) => store.screen.mobile,
    }),

    ...mapState(useAccessibilityStore, {
      previousElement: (store) => store.previousElement,
      getElementInFocusTrap: 'getElementInFocusTrap',
      getFirstElement: 'getFirstElement',
    }),

    ...mapState(useOverlaysStore, ['getActiveOverlay', 'getOverlay']),

    /**
     * Compute overlay active state.
     * @returns {Boolean}
     */
    isActive() {
      return this.getActiveOverlay?.namespace === this.namespace;
    },

    /**
     * Compute overlay object stored in Pinia state.
     * @returns {Object}
     */
    overlay() {
      return this.getOverlay(this.namespace);
    },

    /**
     * Compute responsive direction based on mobile.
     * @returns {String}
     */
    responsiveDirection() {
      if (!this.direction.includes('-')) {
        return this.direction;
      }

      return this.isMobile
        ? this.direction.split('-')[0]
        : this.direction.split('-')[1];
    },

    /**
     * Compute responsive type based on mobile.
     * @returns {String}
     */
    responsiveType() {
      if (!this.type.includes('-')) {
        return this.type;
      }

      return this.isMobile ? this.type.split('-')[0] : this.type.split('-')[1];
    },

    /**
     * Compute overlay API.
     * @returns {Object}
     */
    self() {
      return {
        ...this.$data,
        ...this.$props,
        addCallback: this.addCallback,
        close: this.close,
        isActive: this.isActive,
        open: (args) =>
          this.openOverlay({ namespace: this.namespace, ...args }),
        refresh: this.refresh,
        // Update is deprecated, use refresh instead
        update: this.refresh,
      };
    },

    /**
     * Compute transition name based on type.
     * @returns {String}
     */
    transitionName() {
      const type = this.responsiveType === 'modal' ? 'fade' : 'slide';
      return `${type}-${this.responsiveDirection}`;
    },
  },

  watch: {
    /**
     * Watch hydrated state to start auto-show countdown.
     */
    hydrated() {
      this.setAutoShow();
    },

    /**
     * Watch active state.
     * - Separate logic for event binding.
     */
    async isActive() {
      if (this.noContent) {
        return;
      }

      /**
       * If active then:
       * - Set previous element (to focus on close).
       * - Disable body scroll (if applicable).
       * - Reset scroll element scroll position.
       * - Trap focus inside overlay element.
       * - Focus first trap focus element.
       */
      if (this.isActive) {
        this.setPreviousElement(document.activeElement);

        if (this.disableScroll) {
          this.getScrollElement();
          disableBodyScroll(this.scrollElement, this.bodyScrollOptions());

          /**
           * `html` carries an explicit `overflow-x` below the `l` breakpoint
           * (see critical.scss), which disables CSS's body-to-viewport
           * overflow propagation entirely. body-scroll-lock's non-iOS
           * strategy only sets `overflow: hidden` on `body` and relies on
           * that propagation to clamp the viewport, so it no longer clips
           * anything there - lock `html` directly instead. iOS is
           * unaffected as it locks via touchmove prevention, not overflow.
           */
          document.documentElement.style.overflowY = 'hidden';
        }

        await this.$nextTick();

        if (this.scrollElement) {
          this.scrollElement.scrollTo(0, 0);
        }

        this.updateFocusTrap();
        return;
      }

      if (this.disableScroll) {
        if (this.scrollElement) {
          enableBodyScroll(this.scrollElement);
        } else {
          clearAllBodyScrollLocks();
        }
        document.documentElement.style.overflowY = '';
      }

      /**
       * If no other active overlay when closing then clear focus trap.
       */
      if (!this.getActiveOverlay) {
        this.clearFocusTrap();
      }

      /**
       * Set focus back to previous element if set.
       * - Also reset previous element state to false.
       */
      if (!this.previousElement || typeof this.previousElement !== 'object') {
        return;
      }

      cnvs.Focus(this.previousElement);
      this.setPreviousElement();
    },
  },

  mounted() {
    this.setEventBusListeners();
    this.setAutoShow();
  },

  methods: {
    /**
     * Map Pinia actions.
     */

    ...mapActions(useAccessibilityStore, {
      clearFocusTrap: 'clearFocusTrap',
      setPreviousElement: 'setPreviousElement',
      setFocusTrap: 'setFocusTrap',
    }),

    ...mapActions(useOverlaysStore, {
      closeOverlay: 'close',
      openOverlay: 'open',
      queueOverlay: 'queue',
      updateOverlay: 'update',
    }),

    /**
     * Get scroll element.
     */
    getScrollElement() {
      if (!this.$refs.scrollElement) {
        this.scrollElement = false;
        return;
      }

      const customScrollElement = this.$refs.scrollElement.querySelector(
        '[js-overlay="scrollElement"]',
      );

      if (customScrollElement) {
        this.scrollElement = customScrollElement;
        return;
      }

      const overlayBody =
        this.$refs.scrollElement.querySelector('.overlay__body');

      if (overlayBody) {
        const { overflowY } = window.getComputedStyle(overlayBody);

        if (['auto', 'scroll'].includes(overflowY)) {
          this.scrollElement = overlayBody;
          return;
        }
      }

      this.scrollElement = this.$refs.scrollElement;
    },

    /**
     * Set modal to autoshow.
     */
    async setAutoShow() {
      if (!this.autoShow || !this.hydrated) {
        return;
      }

      await cnvs.Sleep(this.autoShow);

      if (this.autoQueue) {
        this.queueOverlay({ namespace: this.namespace });
        return;
      }

      this.openOverlay({ namespace: this.namespace });
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('cnvs:esc', (overlay) => {
        if (overlay?.namespace !== this.namespace || !this.isActive) {
          return;
        }

        this.close('esc');
      });

      cnvs.EventBus.listen('overlay:refresh', (payload) => {
        if (!this.isActive || payload.namespace !== this.namespace) {
          return;
        }

        this.refresh(payload);
      });
    },

    /**
     * Refresh body scroll element and focus trap.
     * - Can only be triggered when overlay is active.
     * @param {Object} [args] - Arguments passed to overlay API refresh.
     */
    async refresh(args) {
      if (!this.isActive) {
        return;
      }

      await this.$nextTick();
      this.updateFocusTrap(args);
      this.updateBodyScroll();
    },

    /**
     * Update focus trap using Pinia.
     * @param {HTMLElement} [currentElement] - Current focussed element to
     * maintain focus on whilst updating focus trap, if it still exists.
     * @param {Object} [focusElements] - Additional focus trap elements, these
     * are used instead of any focusElements defined when opening the overlay.
     * See overlays.js for the JSDoc definition of focusElements.
     */
    updateFocusTrap({ currentElement, focusElements } = {}) {
      const usedfocusElements = focusElements
        ? focusElements
        : this.overlay?.focusElements || {};

      const config = {
        ...usedfocusElements,
        container: [this.$refs.overlay],
      };

      if (Array.isArray(usedfocusElements?.container)) {
        config.container = usedfocusElements.prepend
          ? [...usedfocusElements.container, this.$refs.overlay]
          : [this.$refs.overlay, ...usedfocusElements.container];
      }

      this.setFocusTrap(config);

      /**
       * Update focussed element.
       * - Have to use timeout to wait for animation to complete as Safari
       *   doesn't support `preventScroll`.
       * - Without timeout the focus would jump instead of smoothly scroll.
       */
      const elementToFocus = this.getElementToFocus(currentElement);

      if (!elementToFocus) {
        return;
      }

      setTimeout(() => {
        elementToFocus.focus({ preventScroll: true });
      }, this.$timing('normal'));
    },

    /**
     * Get element to focus inside focus trap.
     * @param {HTMLElement} [currentElement] - Current focussed element to
     * maintain focus on whilst updating focus trap, if it still exists.
     * @returns {HTMLElement|Null}
     */
    getElementToFocus(currentElement) {
      if (!currentElement || !this.getElementInFocusTrap(currentElement)) {
        return this.getFirstElement;
      }

      return currentElement;
    },

    /**
     * Update body scroll element.
     */
    updateBodyScroll() {
      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement);
      } else {
        clearAllBodyScrollLocks();
      }

      this.getScrollElement();

      disableBodyScroll(this.scrollElement, this.bodyScrollOptions());
    },

    /**
     * Define body scroll options.
     * - Adds support for [js-body-scroll-lock-ignore] attribute which allows
     *   element to maintain scrolling when disableBodyScroll is enabled.
     * @returns {Object}
     */
    bodyScrollOptions() {
      return {
        allowTouchMove: (element) => {
          const ignoredElement = element.closest(
            '[js-body-scroll-lock-ignore]',
          );
          return ignoredElement;
        },
      };
    },

    /**
     * Add callback function to run after close.
     * @param {Function} callback - Callback function to run on close.
     */
    addCallback(callback) {
      this.updateOverlay({
        namespace: this.overlay?.namespace,
        update: { callback },
      });
    },

    /**
     * Handle overlay close.
     * - If no type is passed then it defaults to `button`.
     * @param {String} [type] - Type of close to pass to the callback.
     */
    close(type) {
      if (type !== 'overlay' || !this.ignoreClose) {
        this.closeOverlay({ namespace: this.namespace, type });
      }
    },
  },
};
</script>
